import { apiCall } from '../api';

// ==================== Type Definitions ====================

export const UpdateKindValues = {
  REQUEST: 'request',
  DECISION: 'decision',
  ANNOUNCEMENT: 'announcement',
  BLOCKER: 'blocker',
  MILESTONE: 'milestone',
  INFO: 'info',
} as const;

export type UpdateKind = (typeof UpdateKindValues)[keyof typeof UpdateKindValues];

export const TaskStatusValues = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  DONE: 'done',
  CANCELLED: 'cancelled',
} as const;

export type TaskStatus = (typeof TaskStatusValues)[keyof typeof TaskStatusValues];

export const PriorityValues = {
  LOW: 'low',
  MED: 'med',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

export type Priority = (typeof PriorityValues)[keyof typeof PriorityValues];

export interface TopicUpdate {
  ts: string;
  kind: UpdateKind;
  detail: string;
  sources: string[];
}

export interface TopicEntry {
  topic_id: string;
  title: string;
  thread_ids: string[];
  related_subjects: string[];
  updates: TopicUpdate[];
}

export interface TaskHistoryEntry {
  ts: string;
  change: string;
}

export interface TaskItem {
  task_id: string;
  title: string;
  status: TaskStatus;
  owner?: string;
  due?: string;
  priority: Priority;
  topic_ref?: string;
  sources: string[];
  notes?: string;
  history: TaskHistoryEntry[];
}

export interface Ledger {
  last_processed_ts?: string;
  processed_ids: string[];
}

export interface AuditStats {
  emails_processed: number;
  updates_applied: number;
  tasks_open: number;
  tasks_done_today: number;
}

export interface Audit {
  last_run_id?: string;
  last_run_at?: string;
  stats: AuditStats;
}

export interface DailyLog {
  id: string;
  username: string;
  context_name: string;
  log_date: string;
  summary: string;
  topics: TopicEntry[];
  tasks: TaskItem[];
  ledger: Ledger;
  audit: Audit;
  version: number;
  created_at: string;
  updated_at: string;
}

// ==================== API Functions ====================

/**
 * Get or create the current daily log (for today)
 */
export async function getCurrentDailyLog(contextName: string = 'default'): Promise<DailyLog> {
  return await apiCall<DailyLog>('GET', `/api/dailylog/current?context_name=${contextName}`);
}

/**
 * Get the previous daily log (for yesterday), returns null if it doesn't exist
 */
export async function getPreviousDailyLog(
  contextName: string = 'default',
): Promise<DailyLog | null> {
  try {
    return await apiCall<DailyLog>('GET', `/api/dailylog/previous?context_name=${contextName}`);
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get a daily log for a specific date
 */
export async function getDailyLogByDate(
  logDate: string,
  contextName: string = 'default',
  createIfMissing: boolean = false,
): Promise<DailyLog | null> {
  const params = new URLSearchParams({
    log_date: logDate,
    context_name: contextName,
    create_if_missing: createIfMissing.toString(),
  });

  try {
    return await apiCall<DailyLog>('GET', `/api/dailylog/by-date?${params}`);
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get daily logs for a date range
 */
export async function getDailyLogsByRange(
  startDate: string,
  endDate: string,
  contextName: string = 'default',
  limit: number = 30,
  oldestFirst: boolean = true,
): Promise<DailyLog[]> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    context_name: contextName,
    limit: limit.toString(),
    oldest_first: oldestFirst.toString(),
  });

  return await apiCall<DailyLog[]>('GET', `/api/dailylog/range?${params}`);
}

// ==================== Utility Functions ====================

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  // Parse YYYY-MM-DD as local date, not UTC
  // This prevents timezone conversion issues that would show the wrong date
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Get CSS class for task status
 */
export function getTaskStatusClass(status: TaskStatus): string {
  switch (status) {
  case TaskStatusValues.DONE:
    return 'success';
  case TaskStatusValues.BLOCKED:
    return 'danger';
  case TaskStatusValues.IN_PROGRESS:
    return 'primary';
  case TaskStatusValues.CANCELLED:
    return 'neutral';
  default:
    return 'warning';
  }
}

/**
 * Get CSS class for priority
 */
export function getPriorityClass(priority: Priority): string {
  switch (priority) {
  case PriorityValues.URGENT:
    return 'danger';
  case PriorityValues.HIGH:
    return 'warning';
  case PriorityValues.MED:
    return 'primary';
  default:
    return 'neutral';
  }
}

/**
 * Get icon name for update kind
 */
export function getUpdateKindIcon(kind: UpdateKind): string {
  switch (kind) {
  case UpdateKindValues.REQUEST:
    return 'chat-left-text';
  case UpdateKindValues.DECISION:
    return 'check-circle';
  case UpdateKindValues.ANNOUNCEMENT:
    return 'megaphone';
  case UpdateKindValues.BLOCKER:
    return 'exclamation-triangle';
  case UpdateKindValues.MILESTONE:
    return 'flag';
  default:
    return 'info-circle';
  }
}

/**
 * Format timestamp as "time ago" (e.g., "5 minutes ago", "2 hours ago")
 */
export function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 30) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  }
}
